/**
 * Telefon Aramaları Servisi
 * Paket sipariş sistemi için telefon aramaları ve müşteri yönetimi
 */

import { salesApi, accountingApi } from '../config/apiClient';
import { SALESAPI_URL } from '../config/api';

// ============================================================================
// Paket Müşteri Autocomplete arama sonucu (AccountController.SearchPackageCustomers)
// ============================================================================
export interface PackageCustomerSearchItem {
  accountId: number;
  customerName: string;
  phone?: string | null;
  mobilePhone?: string | null;
}

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface PhoneCallDto {
  Id: number;
  CompanyId: string;
  BranchId?: string;
  PackageScreenId?: number;
  CallerPhone: string;
  CallTime: string;
  CallEndTime?: string;
  CallDuration?: number;
  CallType: 'incoming' | 'outgoing' | 'missed';
  IsAnswered: boolean;
  AccountId?: number;
  IsNewCustomer: boolean;
  OrderId?: number;
  Notes?: string;
  CreatedAt: string;
  CustomerName?: string;
  CustomerPhone?: string;
}

export interface CreatePhoneCallDto {
  CompanyId: string;
  BranchId?: string;
  PackageScreenId?: number;
  CallerPhone: string;
  CallType?: 'incoming' | 'outgoing' | 'missed';
  IsAnswered?: boolean;
  AccountId?: number;
  Notes?: string;
}

export interface UpdatePhoneCallDto {
  CallEndTime?: string;
  CallDuration?: number;
  IsAnswered?: boolean;
  AccountId?: number;
  IsNewCustomer?: boolean;
  OrderId?: number;
  Notes?: string;
}

export interface ShippingAddressDto {
  Id: number;
  AccountId: number;
  AddressLabel?: string;
  Country: string;
  City: string;
  District: string;
  Address: string;
  AuthorizedPerson: string;
  Phone?: string;
  MobilePhone?: string;
  PostalCode?: string;
  IsDefault: boolean;
  Latitude?: number;
  Longitude?: number;
  DeliveryNotes?: string;
  DeliveryZone?: string;
  EstimatedDeliveryMinutes?: number;
  IsActive: boolean;
  Mahalle?: string;
  Cadde?: string;
  Sokak?: string;
  Apartman?: string;
  SiteAdi?: string;
  Blok?: string;
  BinaNo?: string;
  Kat?: string;
  DaireNo?: string;
}

export interface PhoneCustomerSearchResult {
  Found: boolean;
  AccountId?: number;
  CustomerName?: string;
  Phone?: string;
  MobilePhone?: string;
  Email?: string;
  TotalOrders: number;
  LastOrderDate?: string;
  Addresses: ShippingAddressDto[];
}

export interface CreatePackageCustomerDto {
  BranchId?: string;
  CustomerName: string;
  Phone: string;
  MobilePhone?: string;
  Email?: string;
  Notes?: string;
  GroupId: number;
  AddressLabel?: string;
  Country?: string;
  City: string;
  District: string;
  Address: string;
  DeliveryNotes?: string;
  DeliveryZone?: string;
  Mahalle?: string;
  Cadde?: string;
  Sokak?: string;
  Apartman?: string;
  SiteAdi?: string;
  Blok?: string;
  BinaNo?: string;
  Kat?: string;
  DaireNo?: string;
}

export interface CreateShippingAddressDto {
  AccountId: number;
  AddressLabel?: string;
  Country?: string;
  City: string;
  District: string;
  Address: string;
  AuthorizedPerson: string;
  Phone?: string;
  MobilePhone?: string;
  PostalCode?: string;
  IsDefault?: boolean;
  Latitude?: number;
  Longitude?: number;
  DeliveryNotes?: string;
  DeliveryZone?: string;
  EstimatedDeliveryMinutes?: number;
  Mahalle?: string;
  Cadde?: string;
  Sokak?: string;
  Apartman?: string;
  SiteAdi?: string;
  Blok?: string;
  BinaNo?: string;
  Kat?: string;
  DaireNo?: string;
}

export interface BulkCreateCustomerItemDto {
  CustomerName: string;
  Phone?: string;
  MobilePhone?: string;
  FirmName?: string;
  Country?: string;
  City?: string;
  District?: string;
  Address?: string;
  DeliveryZone?: string;
  Mahalle?: string;
  Cadde?: string;
  Sokak?: string;
  Apartman?: string;
  SiteAdi?: string;
  Blok?: string;
  BinaNo?: string;
  Kat?: string;
  DaireNo?: string;
}

export interface BulkCreateCustomerDto {
  BranchId?: string;
  GroupId: string;
  Items: BulkCreateCustomerItemDto[];
}

export interface BulkCreateCustomerResultDto {
  TotalCount: number;
  SuccessCount: number;
  FailedCount: number;
  NewAccountCount: number;
  NewAddressCount: number;
  UpdatedAccountCount: number;
  FailedItems: Array<{
    CustomerName: string;
    Phone?: string;
    Error: string;
  }>;
}

export interface PackageGroupCheckResult {
  exists: boolean;
  groupId?: number;
  groupName?: string;
  message?: string;
}

export interface CreateCustomerResult {
  success: boolean;
  accountId: number;
  customerName: string;
  addressId: number;
  address: ShippingAddressDto;
}

// ============================================================================
// API ENDPOINTS
// ============================================================================

const API_BASE = `${SALESAPI_URL}/api/PhoneCall`;

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Bugünkü aramaları getirir
 */
export const getTodayCalls = async (
  companyId: string,
  packageScreenId?: number
): Promise<PhoneCallDto[]> => {
  const params = packageScreenId ? `?packageScreenId=${packageScreenId}` : '';
  const response = await salesApi.get(`${API_BASE}/today${params}`);
  return response.data;
};

/**
 * Cevaplanmamış aramaları getirir
 */
export const getMissedCalls = async (
  companyId: string,
  packageScreenId?: number
): Promise<PhoneCallDto[]> => {
  const params = packageScreenId ? `?packageScreenId=${packageScreenId}` : '';
  const response = await salesApi.get(`${API_BASE}/missed${params}`);
  return response.data;
};

/**
 * Telefon numarasına göre müşteri arar
 * ✅ ÖNEMLİ: Gelen arama için müşteri eşleştirme
 */
export const searchCustomerByPhone = async (
  companyId: string,
  phone: string
): Promise<PhoneCustomerSearchResult> => {
  const response = await salesApi.get(`${API_BASE}/search-customer`, {
    params: { phone }
  });
  return response.data;
};

/**
 * Paket sipariş ekranındaki "Müşteri Ara" modalı için autocomplete araması.
 * Backend `AccountController.SearchPackageCustomers` (min 2 karakter zorunlu,
 * CompanyName + Phone + MobilePhone içinde Contains ile arama, limit ile sınırlı).
 *
 * Çağıran tarafta debounce uygulanmalı (önerilen 300ms).
 */
export const searchPackageCustomers = async (
  searchTerm: string,
  limit = 20
): Promise<PackageCustomerSearchItem[]> => {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return [];
  }
  const response = await accountingApi.get('/api/Account/search-package-customers', {
    params: { q: searchTerm.trim(), limit },
  });
  const list = response?.data?.data;
  return Array.isArray(list) ? (list as PackageCustomerSearchItem[]) : [];
};

/**
 * "Paket Müşterisi" cari grubunu kontrol eder
 */
export const checkPackageCustomerGroup = async (
  companyId: string
): Promise<PackageGroupCheckResult> => {
  const response = await salesApi.get(`${API_BASE}/check-package-group`);
  return response.data;
};

/**
 * Yeni arama kaydı oluşturur
 */
export const createPhoneCall = async (
  data: CreatePhoneCallDto
): Promise<PhoneCallDto> => {
  const response = await salesApi.post(`${API_BASE}/create`, data);
  return response.data;
};

/**
 * Arama kaydını günceller
 */
export const updatePhoneCall = async (
  id: number,
  data: UpdatePhoneCallDto
): Promise<{ success: boolean; id: number }> => {
  const response = await salesApi.post(`${API_BASE}/update/${id}`, data);
  return response.data;
};

/**
 * Yeni paket müşterisi oluşturur (Account + ShippingAddress)
 */
export const createPackageCustomer = async (
  data: CreatePackageCustomerDto
): Promise<CreateCustomerResult> => {
  const response = await salesApi.post(`${API_BASE}/create-package-customer`, data);
  return response.data;
};

/**
 * "Paket Müşterisi" cari grubunu oluşturur
 */
export const createPackageCustomerGroup = async (
  companyId: string,
  branchId?: string
): Promise<PackageGroupCheckResult> => {
  const response = await salesApi.post(`${API_BASE}/create-package-group`, {
    BranchId: branchId
  });
  return response.data;
};

/**
 * Müşteriye yeni sevk adresi ekler
 */
export const addShippingAddress = async (
  data: CreateShippingAddressDto
): Promise<ShippingAddressDto> => {
  const response = await salesApi.post(`${API_BASE}/add-address`, data);
  return response.data;
};

/**
 * Toplu müşteri aktarımı (Excel import)
 */
export const bulkCreateCustomers = async (
  data: BulkCreateCustomerDto
): Promise<BulkCreateCustomerResultDto> => {
  const response = await salesApi.post(`${API_BASE}/bulk-create-customers`, data);
  return response.data;
};

export interface CheckExistingPhonesResult {
  ExistingCount: number;
  NewCount: number;
  TotalChecked: number;
  ExistingPhones: string[];
}

/**
 * Toplu telefon numarası kontrolü - önizlemede mevcut müşterileri göstermek için
 */
export const checkExistingPhones = async (
  phones: string[]
): Promise<CheckExistingPhonesResult> => {
  const response = await salesApi.post(`${API_BASE}/check-existing-phones`, { Phones: phones });
  return response.data;
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const phoneCallService = {
  getTodayCalls,
  getMissedCalls,
  searchCustomerByPhone,
  searchPackageCustomers,
  checkPackageCustomerGroup,
  createPhoneCall,
  updatePhoneCall,
  createPackageCustomer,
  createPackageCustomerGroup,
  addShippingAddress,
  bulkCreateCustomers,
  checkExistingPhones,
};

export default phoneCallService;
